import type {
  EscalationAuditCause,
  EscalationAuditPage,
} from "@/types/escalationAudit";
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect } from "react";

import { apiFetch } from "./client";
import type {
  ActiveOrdersResponse,
  ActiveOverTimeResponse,
  AdminKpisResponse,
  AdminListResponse,
  AdminOverviewSnapshotResponse,
  CohortCycleFilter,
  CohortRetentionResponse,
  CustomerDetailResponse,
  DailySignupsResponse,
  FeatureFlagsResponse,
  GrantAdminResponse,
  PharmacyEventsResponse,
  PharmacyWarningDismissBody,
  PharmacyWarningInclude,
  PharmacyWarningResolveBody,
  PharmacyWarningsResponse,
  AdminRole,
  RefreshResponse,
  SetAdminRoleResponse,
  RevokeAdminResponse,
  SubscriptionsListResponse,
  SyncStatusResponse,
} from "@/types/admin";
import type { AICapabilitiesResponse } from "@/types/aiCapabilities";
import type {
  AITicketOutcomesResponse,
  AIOutcomeWindow,
} from "@/types/aiOutcomes";
import type { AIRuntimeStatusResponse } from "@/types/aiRuntimeStatus";
import type {
  CustomerServiceMetrics,
  CustomerServiceStatusSummary,
  ServiceFilters,
} from "@/types/customerServiceMetrics";
import {
  adaptAIInventorySummary,
  adaptAutomationSummary,
} from "@/lib/api/systemOpsAdapters";

function adminTimezone(): string {
  return typeof Intl !== "undefined"
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : "";
}

export function adminOverviewQueryOptions() {
  const tz = adminTimezone();
  const params = new URLSearchParams();
  if (tz) params.set("tz", tz);
  const qs = params.toString();
  return {
    queryKey: ["admin", "overview-snapshot", tz] as const,
    queryFn: () =>
      apiFetch<AdminOverviewSnapshotResponse>(
        `/admin/analytics/overview-snapshot${qs ? `?${qs}` : ""}`,
      ),
    staleTime: 60_000,
  };
}

export function useAdminOverviewSnapshot() {
  return useQuery(adminOverviewQueryOptions());
}

export function useAICapabilities({
  enabled = true,
}: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["admin", "ai-capabilities"],
    queryFn: () => apiFetch<AICapabilitiesResponse>("/admin/ai-capabilities"),
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useAIRuntimeStatus({
  enabled = true,
}: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["admin", "ai-runtime-status"],
    queryFn: () =>
      apiFetch<AIRuntimeStatusResponse>("/admin/ai-runtime-status"),
    staleTime: 60_000,
    enabled,
  });
}

export function useAIOutcomes(
  window: AIOutcomeWindow = "7d",
  { enabled = true }: { enabled?: boolean } = {},
) {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    // Four tiny aggregate responses cover every channel. Warm alternate
    // windows once so switching filters reads from the same query cache.
    for (const alternate of ["all", "30d", "7d", "24h"] as const) {
      if (alternate === window) continue;
      void queryClient.prefetchQuery({
        queryKey: ["admin", "ai-outcomes", alternate],
        queryFn: () =>
          apiFetch<AITicketOutcomesResponse>(
            `/admin/ai-outcomes?window=${alternate}&view=tickets`,
          ),
        staleTime: 60_000,
      });
    }
  }, [enabled, queryClient, window]);
  return useQuery({
    queryKey: ["admin", "ai-outcomes", window],
    queryFn: () =>
      apiFetch<AITicketOutcomesResponse>(
        `/admin/ai-outcomes?window=${window}&view=tickets`,
      ),
    staleTime: 60_000,
    enabled,
  });
}

export function customerServiceQueryOptions(filters: ServiceFilters) {
  const params = new URLSearchParams({ ...filters });
  return {
    queryKey: ["admin", "customer-service-history", filters] as const,
    queryFn: () =>
      apiFetch<CustomerServiceMetrics>(
        `/admin/customer-service-history?${params}`,
      ),
    // Warehouse aggregates change slowly and take a few seconds to calculate.
    // Reuse a verified filter result briefly, and retain the prior shape while
    // a new selection loads so the dashboard can label it explicitly.
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    placeholderData: keepPreviousData,
  };
}

export function useCustomerServiceMetrics(filters: ServiceFilters) {
  return useQuery(customerServiceQueryOptions(filters));
}

export function customerServiceStatusQueryOptions(filters: ServiceFilters) {
  const params = new URLSearchParams({ ...filters });
  return {
    queryKey: ["admin", "customer-service-status-summary", filters] as const,
    queryFn: () =>
      apiFetch<CustomerServiceStatusSummary>(
        `/admin/customer-service-history/status-summary?${params}`,
      ),
    staleTime: 60_000,
    gcTime: 30 * 60_000,
  };
}

export function useCustomerServiceStatusSummary(filters: ServiceFilters) {
  return useQuery(customerServiceStatusQueryOptions(filters));
}

export function useSystemOpsAIInventory() {
  return useQuery({
    queryKey: ["admin", "system-ops", "ai-capabilities"],
    queryFn: async () =>
      adaptAIInventorySummary(
        await apiFetch<unknown>("/admin/ai-capabilities"),
      ),
    staleTime: 5 * 60_000,
  });
}

export function useSystemOpsAutomationSummary() {
  return useQuery({
    queryKey: ["admin", "system-ops", "operations"],
    queryFn: async () =>
      adaptAutomationSummary(await apiFetch<unknown>("/admin/operations")),
    staleTime: 60_000,
  });
}

export function useAdminKpis({ enabled = true } = {}) {
  return useQuery({
    queryKey: ["admin", "kpis"],
    queryFn: () => apiFetch<AdminKpisResponse>("/admin/analytics/kpis"),
    staleTime: 60_000,
    enabled,
  });
}

export function useDailySignups({
  days = 14,
  weeks = 4,
  topProducts = 6,
  granularity = "daily" as "daily" | "weekly",
  enabled = true,
} = {}) {
  // Browser's IANA timezone (e.g., "America/Los_Angeles"). The backend
  // buckets by this so the "today" column matches the user's wall clock
  // regardless of where the server runs.
  const tz = adminTimezone();
  const params = new URLSearchParams({
    days: String(days),
    weeks: String(weeks),
    top_products: String(topProducts),
    granularity,
  });
  if (tz) params.set("tz", tz);
  return useQuery({
    queryKey: [
      "admin",
      "daily-signups",
      granularity,
      days,
      weeks,
      topProducts,
      tz,
    ],
    queryFn: () =>
      apiFetch<DailySignupsResponse>(
        `/admin/analytics/daily-signups?${params}`,
      ),
    staleTime: 60_000,
    placeholderData: (previousData) => previousData,
    enabled,
  });
}

export function useActiveOverTime({
  weeks = 12,
  cycle = "all" as const,
  topProducts = 6,
  enabled = true,
}: {
  weeks?: number;
  cycle?: "monthly" | "quarterly" | "all";
  topProducts?: number;
  enabled?: boolean;
} = {}) {
  const params = new URLSearchParams({
    weeks: String(weeks),
    cycle,
    top_products: String(topProducts),
  });
  return useQuery({
    queryKey: ["admin", "active-over-time", weeks, cycle, topProducts],
    queryFn: () =>
      apiFetch<ActiveOverTimeResponse>(
        `/admin/analytics/active-over-time?${params}`,
      ),
    staleTime: 60_000,
    placeholderData: (previousData) => previousData,
    enabled,
  });
}

interface UseCohortRetentionArgs {
  cycle: CohortCycleFilter;
  days?: number;
  cap?: number;
  product?: string | null;
  productLabel?: string | null;
  groupBy?: "product_id" | "label";
  enabled?: boolean;
}

export function useCohortRetention({
  cycle,
  days = 180,
  cap = 6,
  product,
  productLabel,
  groupBy = "label",
  enabled = true,
}: UseCohortRetentionArgs) {
  const params = new URLSearchParams({
    cycle,
    days: String(days),
    cap: String(cap),
    group_by: groupBy,
  });
  if (product) params.set("product", product);
  if (productLabel) params.set("product_label", productLabel);
  const qs = params.toString();
  return useQuery({
    queryKey: [
      "admin",
      "cohort-retention",
      cycle,
      days,
      cap,
      product ?? null,
      productLabel ?? null,
      groupBy,
    ],
    queryFn: () =>
      apiFetch<CohortRetentionResponse>(
        `/admin/analytics/cohort-retention?${qs}`,
      ),
    staleTime: 60_000,
    enabled,
  });
}

interface UseCohortCancellationArgs {
  cycle: CohortCycleFilter;
  days?: number;
  cap?: number;
  product?: string | null;
  productLabel?: string | null;
  groupBy?: "product_id" | "label";
  enabled?: boolean;
}

export function useCohortCancellation({
  cycle,
  days = 180,
  cap = 4,
  product,
  productLabel,
  groupBy = "label",
  enabled = true,
}: UseCohortCancellationArgs) {
  const params = new URLSearchParams({
    cycle,
    days: String(days),
    cap: String(cap),
    group_by: groupBy,
  });
  if (product) params.set("product", product);
  if (productLabel) params.set("product_label", productLabel);
  const qs = params.toString();
  return useQuery({
    queryKey: [
      "admin",
      "cohort-cancellation",
      cycle,
      days,
      cap,
      product ?? null,
      productLabel ?? null,
      groupBy,
    ],
    queryFn: () =>
      apiFetch<CohortRetentionResponse>(
        `/admin/analytics/cohort-cancellation?${qs}`,
      ),
    staleTime: 60_000,
    enabled,
  });
}

export function useSyncStatus() {
  return useQuery({
    queryKey: ["admin", "sync", "status"],
    queryFn: () => apiFetch<SyncStatusResponse>("/admin/sync/status"),
    // Poll faster while a sync is running so the badge animates out when it
    // finishes, but don't hammer the endpoint the rest of the time.
    refetchInterval: (query) =>
      (query.state.data as SyncStatusResponse | undefined)?.running
        ? 3_000
        : false,
    staleTime: 15_000,
  });
}

export interface SubscriptionsFilter {
  status?: string | null;
  product?: string | null;
  productLabel?: string | null;
  search?: string | null;
  createdAfter?: string | null;
  createdBefore?: string | null;
  minPrice?: number | null;
  maxPrice?: number | null;
  /** Cohort drill-down: keep only subs with >=N paid subscription_cycle renewals. */
  paidRenewalsMin?: number | null;
  /** Cohort drill-down: keep only subs with <=N paid subscription_cycle renewals
   *  (i.e. churned by M_(N+1) — did not retain to that bar). */
  paidRenewalsMax?: number | null;
  /** Cohort drill-down: keep only subs whose cancelled_at lands in week N post-signup. */
  cancelWeek?: number | null;
}

function buildSubscriptionParams(
  filter: SubscriptionsFilter,
  { limit, offset }: { limit?: number; offset?: number } = {},
) {
  const params = new URLSearchParams();
  if (limit != null) params.set("limit", String(limit));
  if (offset != null) params.set("offset", String(offset));
  if (filter.status) params.set("status", filter.status);
  if (filter.product) params.set("product", filter.product);
  if (filter.productLabel) params.set("product_label", filter.productLabel);
  if (filter.search) params.set("search", filter.search);
  if (filter.createdAfter) params.set("created_after", filter.createdAfter);
  if (filter.createdBefore) params.set("created_before", filter.createdBefore);
  if (filter.minPrice != null) params.set("min_price", String(filter.minPrice));
  if (filter.maxPrice != null) params.set("max_price", String(filter.maxPrice));
  if (filter.paidRenewalsMin != null)
    params.set("paid_renewals_min", String(filter.paidRenewalsMin));
  if (filter.paidRenewalsMax != null)
    params.set("paid_renewals_max", String(filter.paidRenewalsMax));
  if (filter.cancelWeek != null)
    params.set("cancel_week", String(filter.cancelWeek));
  return params;
}

export function useAdminSubscriptions(
  filter: SubscriptionsFilter,
  { limit = 50, offset = 0 }: { limit?: number; offset?: number } = {},
) {
  const params = buildSubscriptionParams(filter, { limit, offset });
  return useQuery({
    queryKey: ["admin", "subscriptions", Object.fromEntries(params.entries())],
    queryFn: () =>
      apiFetch<SubscriptionsListResponse>(
        `/admin/analytics/subscriptions?${params}`,
      ),
    staleTime: 30_000,
  });
}

export function useProductLabels() {
  return useQuery({
    queryKey: ["admin", "subscriptions", "product-labels"],
    queryFn: () =>
      apiFetch<string[]>("/admin/analytics/subscriptions/product-labels"),
    staleTime: 5 * 60_000,
  });
}

export function buildSubscriptionsCsvUrl(filter: SubscriptionsFilter): string {
  // Intentionally does NOT include offset/limit — CSV exports the full
  // filtered set, capped by the backend (50k rows by default).
  const params = buildSubscriptionParams(filter);
  const qs = params.toString();
  return qs
    ? `/admin/analytics/subscriptions/export.csv?${qs}`
    : "/admin/analytics/subscriptions/export.csv";
}

export function useAdminCustomerDetail(
  customerCpId: string | null | undefined,
) {
  return useQuery({
    queryKey: ["admin", "customers", customerCpId],
    queryFn: () =>
      apiFetch<CustomerDetailResponse>(
        `/admin/analytics/customers/${customerCpId}`,
      ),
    enabled: Boolean(customerCpId),
    staleTime: 30_000,
  });
}

export function useTriggerRefresh() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<RefreshResponse>("/admin/sync/refresh", { method: "POST" }),
    onSuccess: () => {
      // Bump the status query immediately so the UI flips into "running"
      // before the next poll fires.
      qc.invalidateQueries({ queryKey: ["admin", "sync", "status"] });
    },
  });
}

export function useAdmins() {
  return useQuery({
    queryKey: ["admin", "admins"],
    queryFn: () => apiFetch<AdminListResponse>("/admin/admins"),
    staleTime: 30_000,
  });
}

export function useGrantAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      email,
      role = "support",
    }: {
      email: string;
      role?: AdminRole;
    }) =>
      apiFetch<GrantAdminResponse>("/admin/admins", {
        method: "POST",
        body: JSON.stringify({ email, role }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "admins"] });
    },
  });
}

export function useRevokeAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patientId: string) =>
      apiFetch<RevokeAdminResponse>(`/admin/admins/${patientId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "admins"] });
    },
  });
}

export function useActiveOrders() {
  return useQuery({
    queryKey: ["admin", "clinical-ops", "active-orders"],
    queryFn: () =>
      apiFetch<ActiveOrdersResponse>("/admin/clinical-ops/active-orders"),
    staleTime: 60_000,
  });
}

export function usePharmacyEvents() {
  return useQuery({
    queryKey: ["admin", "clinical-ops", "pharmacy-events"],
    queryFn: () =>
      apiFetch<PharmacyEventsResponse>("/admin/clinical-ops/pharmacy-events"),
    staleTime: 60_000,
  });
}

export function usePharmacyWarnings(
  include: PharmacyWarningInclude = "unresolved",
  patientId?: string,
) {
  const params = new URLSearchParams({ include });
  if (patientId) params.set("patient_id", patientId);
  return useQuery({
    queryKey: ["admin", "pharmacy", "warnings", include, patientId ?? null],
    queryFn: () =>
      apiFetch<PharmacyWarningsResponse>(
        `/admin/pharmacy/warnings?${params.toString()}`,
      ),
    staleTime: 30_000,
    refetchInterval: patientId ? false : 60_000,
  });
}

export function useResolvePharmacyWarning() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      body,
    }: {
      orderId: string;
      body: PharmacyWarningResolveBody;
    }) =>
      apiFetch<{ resolved: boolean; action_type: string }>(
        `/admin/pharmacy/warnings/${orderId}/resolve`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      // Invalidate every include filter (unresolved/resolved/all).
      qc.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === "admin" &&
          q.queryKey[1] === "pharmacy" &&
          q.queryKey[2] === "warnings",
      });
    },
  });
}

export function useDismissPharmacyWarning() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      body,
    }: {
      orderId: string;
      body: PharmacyWarningDismissBody;
    }) =>
      apiFetch<{ resolved: boolean; action_type: string }>(
        `/admin/pharmacy/warnings/${orderId}/dismiss`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      // Invalidate every include filter (unresolved/resolved/all).
      qc.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === "admin" &&
          q.queryKey[1] === "pharmacy" &&
          q.queryKey[2] === "warnings",
      });
    },
  });
}

// ---------- Awaiting prescription orders (eRx-first lander flow) ----------

export interface AwaitingOrderRow {
  id: string;
  order_number: string | null;
  patient_id: string;
  patient_email: string | null;
  patient_display_name: string | null;
  patient_healthie_id: string | null;
  product_id: string | null;
  partner_key: string | null;
  total_cents: number;
  awaiting_reason: string | null;
  fulfillment_hold: boolean;
  fulfillment_hold_reason: string | null;
  fulfillment_held_at: string | null;
  stripe_invoice_id: string | null;
  stripe_subscription_id: string | null;
  blocking_pharmacy_order_id: string | null;
  created_at: string;
}

export interface AwaitingOrdersResponse {
  auto_submit_enabled: boolean;
  /** Every held order plus the newest unheld ones, or the search matches. */
  orders: AwaitingOrderRow[];
  total_awaiting?: number | null;
  held_total?: number | null;
  /** The search the server applied, if any. */
  query?: string | null;
  /** The search hit its cap; a narrower term shows every match. */
  truncated?: boolean;
}

export interface AwaitingOrderSubmitResult {
  pharmacy_order_id: string | null;
  status: string;
  partner_key: string | null;
  partner_order_id: string | null;
  error: string | null;
}

/** `search` (3+ characters) is matched by the server across every awaiting
 * order, not just the rows already loaded. */
export function useAwaitingOrders(search = "") {
  return useQuery({
    queryKey: ["admin", "pharmacy", "awaiting-orders", search],
    queryFn: () =>
      apiFetch<AwaitingOrdersResponse>(
        "/admin/pharmacy/awaiting-orders" +
          (search ? `?q=${encodeURIComponent(search)}` : ""),
      ),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export interface AwaitingOrderLinePreview {
  product_id: string | null;
  stripe_name: string | null;
  quantity: number;
  unit_price_cents: number;
  pharmacy_product: Record<string, unknown> | null;
  transmit_payload: Record<string, unknown> | null;
}

export interface AwaitingOrderPreviewResponse {
  order_id: string;
  partner_key: string | null;
  order_number: string | null;
  patient: Record<string, unknown>;
  prescriber: Record<string, unknown> | null;
  lines: AwaitingOrderLinePreview[];
  pdf_preview_base64: string | null;
  pdf_preview_filename: string | null;
  healthie_match: Record<string, unknown> | null;
  notes: string[];
}

export function useAwaitingOrderPreview(orderId: string | null) {
  return useQuery({
    queryKey: ["admin", "pharmacy", "awaiting-orders", "preview", orderId],
    queryFn: () =>
      apiFetch<AwaitingOrderPreviewResponse>(
        `/admin/pharmacy/awaiting-orders/${orderId}/preview`,
      ),
    enabled: Boolean(orderId),
    staleTime: 60_000,
  });
}

// ---------- Subscription shipment schedule (multi-fill plans) ----------

export interface ScheduledShipmentRow {
  id: string;
  patient_id: string;
  patient_email: string | null;
  patient_display_name: string | null;
  stripe_subscription_id: string;
  stripe_invoice_id: string;
  product_id: string;
  partner_key: string;
  shipment_index: number;
  total_shipments: number;
  cycle_index: number;
  container_qty: number;
  total_transmitted_qty: number;
  scheduled_for: string;
  status: string;
  pharmacy_order_id: string | null;
  failure_reason: string | null;
  fulfillment_hold: boolean;
  fulfillment_hold_reason: string | null;
  fulfillment_held_at: string | null;
  blocking_stripe_invoice_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ScheduledShipmentsResponse {
  pending: ScheduledShipmentRow[];
  submitted: ScheduledShipmentRow[];
}

export function useSubscriptionFills() {
  return useQuery({
    queryKey: ["admin", "pharmacy", "subscription-fills"],
    queryFn: () =>
      apiFetch<ScheduledShipmentsResponse>(
        "/admin/pharmacy/subscription-fills",
      ),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export interface FireFillResult {
  fill_id: string;
  pharmacy_order_id: string | null;
  status: string;
  error?: string | null;
}

export function useFireSubscriptionFill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fillId }: { fillId: string }) =>
      apiFetch<FireFillResult>(
        `/admin/pharmacy/subscription-fills/${fillId}/fire`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === "admin" &&
          q.queryKey[1] === "pharmacy" &&
          q.queryKey[2] === "subscription-fills",
      });
    },
  });
}

export function useReleaseSubscriptionFillHold() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      fillId,
      idempotencyKey,
      reason,
    }: {
      fillId: string;
      idempotencyKey: string;
      reason: string;
    }) =>
      apiFetch<FireFillResult>(
        `/admin/pharmacy/subscription-fills/${fillId}/release-hold`,
        {
          method: "POST",
          body: JSON.stringify({
            idempotency_key: idempotencyKey,
            reason,
          }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["admin", "pharmacy", "subscription-fills"],
      });
    },
  });
}

export function useSubmitAwaitingOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId }: { orderId: string }) =>
      apiFetch<AwaitingOrderSubmitResult>(
        `/admin/pharmacy/awaiting-orders/${orderId}/submit`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === "admin" &&
          q.queryKey[1] === "pharmacy" &&
          q.queryKey[2] === "awaiting-orders",
      });
    },
  });
}

export function useReleaseAwaitingOrderHold() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      idempotencyKey,
      reason,
    }: {
      orderId: string;
      idempotencyKey: string;
      reason: string;
    }) =>
      apiFetch<AwaitingOrderSubmitResult>(
        `/admin/pharmacy/awaiting-orders/${orderId}/release-hold`,
        {
          method: "POST",
          body: JSON.stringify({
            idempotency_key: idempotencyKey,
            reason,
          }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === "admin" &&
          q.queryKey[1] === "pharmacy" &&
          q.queryKey[2] === "awaiting-orders",
      });
    },
  });
}

export function useFeatureFlags() {
  return useQuery({
    queryKey: ["admin", "feature-flags"],
    queryFn: () => apiFetch<FeatureFlagsResponse>("/admin/feature-flags"),
    staleTime: 15_000,
  });
}

export function useUpdateFeatureFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      key,
      value,
      note,
    }: {
      key: string;
      value: boolean | number;
      note?: string;
    }) =>
      apiFetch<FeatureFlagsResponse>(`/admin/feature-flags/${key}`, {
        method: "PUT",
        body: JSON.stringify({ value, note: note ?? null }),
      }),
    onSuccess: (data) => {
      qc.setQueryData(
        ["admin", "feature-flags"],
        (prev: FeatureFlagsResponse | undefined) =>
          prev ? { ...prev, flags: data.flags } : undefined,
      );
      qc.invalidateQueries({ queryKey: ["admin", "feature-flags"] });
    },
  });
}

export function useClearFeatureFlagOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) =>
      apiFetch<FeatureFlagsResponse>(`/admin/feature-flags/${key}`, {
        method: "DELETE",
      }),
    onSuccess: (data) => {
      qc.setQueryData(
        ["admin", "feature-flags"],
        (prev: FeatureFlagsResponse | undefined) =>
          prev ? { ...prev, flags: data.flags } : undefined,
      );
      qc.invalidateQueries({ queryKey: ["admin", "feature-flags"] });
    },
  });
}

export function useSetAdminRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ patientId, role }: { patientId: string; role: AdminRole }) =>
      apiFetch<SetAdminRoleResponse>(`/admin/admins/${patientId}/role`, {
        method: "PUT",
        body: JSON.stringify({ role }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "admins"] });
    },
  });
}

// Isolated synthetic support runs. Nothing is submitted until the teammate sends.
export function useSupportSimulationCapabilities() {
  return useQuery({
    queryKey: ["admin", "support-simulations", "capabilities"],
    queryFn: () =>
      apiFetch<import("@/types/supportSimulations").SimulationCapabilities>(
        "/admin/support-simulations/capabilities",
      ),
    retry: false,
    staleTime: 30_000,
  });
}

export function useSupportSimulationTurn() {
  return useMutation({
    mutationFn: (body: import("@/types/supportSimulations").SimulationTurn) =>
      apiFetch<import("@/types/supportSimulations").SimulationReply>(
        "/admin/support-simulations/turn",
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      ),
    retry: false,
  });
}

export function escalationAuditQueryOptions(
  cause: EscalationAuditCause,
  offset = 0,
) {
  const params = new URLSearchParams({
    cause,
    limit: "25",
    offset: String(offset),
  });
  return {
    queryKey: ["admin", "escalation-audit", cause, offset] as const,
    queryFn: () =>
      apiFetch<EscalationAuditPage>(
        `/admin/customer-service-history/escalation-audit?${params}`,
      ),
    staleTime: 5 * 60_000,
  };
}

export function useEscalationAuditThreads(
  cause: EscalationAuditCause,
  offset = 0,
) {
  return useQuery(escalationAuditQueryOptions(cause, offset));
}

export function useSupportReplayThreads(enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "support-simulations", "replay", "threads"],
    queryFn: () =>
      apiFetch<import("@/types/supportReplay").ReplayThreads>(
        "/admin/support-simulations/replay/threads",
      ),
    enabled,
    retry: false,
    staleTime: 30_000,
  });
}
export function useStartSupportReplay() {
  return useMutation({
    mutationFn: (body: { thread_id: string; client_run_key: string }) =>
      apiFetch<import("@/types/supportReplay").ReplayRun>(
        "/admin/support-simulations/replay/runs",
        { method: "POST", body: JSON.stringify(body) },
      ),
    retry: false,
  });
}
export function useStepSupportReplay() {
  return useMutation({
    mutationFn: ({
      run_id,
      expected_cursor,
    }: {
      run_id: string;
      expected_cursor: number;
    }) =>
      apiFetch<import("@/types/supportReplay").ReplayRun>(
        `/admin/support-simulations/replay/runs/${encodeURIComponent(run_id)}/step`,
        { method: "POST", body: JSON.stringify({ expected_cursor }) },
      ),
    retry: false,
  });
}
export function usePollSupportReplay() {
  return useMutation({
    mutationFn: (run_id: string) =>
      apiFetch<import("@/types/supportReplay").ReplayRun>(
        `/admin/support-simulations/replay/runs/${encodeURIComponent(run_id)}`,
      ),
    retry: false,
  });
}

// Active inboxes refresh from the protected backend; no browser database subscription.
// Pause hidden tabs and back off failed reads to avoid hammering provider APIs.
const inboxRefreshInterval = (query: { state: { status: string } }) =>
  query.state.status === "error" ? 30_000 : 5_000;

export function useEscalatedInbox(
  channel: import("@/types/escalatedInbox").InboxChannel,
  offset: number,
  actorId: string | undefined,
) {
  const params = new URLSearchParams({
    channel,
    limit: "25",
    offset: String(offset),
  });
  return useQuery({
    queryKey: ["admin", "escalated-inbox", actorId, channel, offset],
    queryFn: ({ signal }) =>
      apiFetch<import("@/types/escalatedInbox").EscalatedThreadPage>(
        `/admin/customer-service-history/escalated-threads?${params}`,
        { signal },
      ),
    enabled: !!actorId,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: inboxRefreshInterval,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
  });
}
export function useEscalatedInboxThread(
  threadId: string | undefined,
  actorId: string | undefined,
) {
  return useInfiniteQuery({
    queryKey: ["admin", "escalated-inbox-thread", actorId, threadId],
    queryFn: ({ pageParam, signal }) =>
      apiFetch<import("@/types/escalatedInbox").EscalatedThreadDetail>(
        `/admin/customer-service-history/escalated-threads/${encodeURIComponent(threadId!)}?message_offset=${pageParam}&message_limit=200`,
        { signal },
      ),
    initialPageParam: 0,
    getNextPageParam: (last) =>
      last.has_more_messages
        ? last.message_offset + last.messages.length
        : undefined,
    enabled: !!actorId && !!threadId,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: inboxRefreshInterval,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
  });
}

export function useSupportReviewThreads(
  channel: import("@/types/supportReview").SupportReviewChannel | "all",
  offset: number,
  actorId: string | undefined,
) {
  const params = new URLSearchParams({
    channel,
    limit: "25",
    offset: String(offset),
  });
  return useQuery({
    queryKey: ["admin", "support-review", actorId, channel, offset],
    queryFn: ({ signal }) =>
      apiFetch<import("@/types/supportReview").SupportReviewQueue>(
        `/admin/support-simulations/review/threads?${params}`,
        { signal },
      ),
    enabled: !!actorId,
    retry: false,
    staleTime: 15_000,
    gcTime: 0,
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
  });
}

export function useInfiniteSupportReviewThreads(
  channel: import("@/types/supportReview").SupportReviewChannel | "all",
  actorId: string | undefined,
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: ["admin", "support-review", actorId, channel, "infinite"],
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams({
        channel,
        limit: "25",
        offset: String(pageParam),
      });
      return apiFetch<import("@/types/supportReview").SupportReviewQueue>(
        `/admin/support-simulations/review/threads?${params}`,
        { signal },
      );
    },
    initialPageParam: 0,
    getNextPageParam: (last) => {
      const nextOffset = last.offset + last.limit;
      return nextOffset < last.total ? nextOffset : undefined;
    },
    enabled: enabled && !!actorId,
    retry: false,
    staleTime: 15_000,
    // Keep only an in-memory, actor-scoped queue for fast return navigation.
    // Stale rows render immediately while mount/focus refreshes verify changes.
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
  });
}

export function useSupportReviewThread(
  threadId: string | undefined,
  actorId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ["admin", "support-review-thread", actorId, threadId],
    queryFn: ({ signal }) =>
      apiFetch<import("@/types/supportReview").SupportReviewDetail>(
        `/admin/support-simulations/review/threads/${encodeURIComponent(threadId!)}`,
        { signal },
      ),
    enabled: enabled && !!actorId && !!threadId,
    retry: false,
    // Thread assembly spans several read-only providers. Keep visited reviews
    // warm so returning to a thread is instant instead of rebuilding context.
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useSupportReviewThreadShell(
  threadId: string | undefined,
  actorId: string | undefined,
) {
  return useQuery({
    queryKey: ["admin", "support-review-thread-shell", actorId, threadId],
    queryFn: ({ signal }) =>
      apiFetch<import("@/types/supportReview").SupportReviewShell>(
        `/admin/support-simulations/review/threads/${encodeURIComponent(threadId!)}/shell`,
        { signal },
      ),
    enabled: !!actorId && !!threadId,
    retry: false,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useSupportReviewSavedDraft(
  threadId: string | undefined,
  actorId: string | undefined,
) {
  return useQuery({
    queryKey: ["admin", "support-review-saved-draft", actorId, threadId],
    queryFn: ({ signal }) =>
      apiFetch<import("@/types/supportReview").SupportReviewSavedDraft>(
        `/admin/support-simulations/review/threads/${encodeURIComponent(threadId!)}/saved-draft`,
        { signal },
      ),
    enabled: !!actorId && !!threadId,
    retry: false,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useSupportReviewWarehouseHistory(
  threadId: string | undefined,
  actorId: string | undefined,
) {
  return useInfiniteQuery({
    queryKey: ["admin", "support-review-warehouse-history", actorId, threadId],
    queryFn: async ({ signal, pageParam }) => {
      const params = new URLSearchParams({ limit: "50" });
      if (pageParam) params.set("cursor", pageParam);
      const history = await apiFetch<
        import("@/types/supportReview").SupportReviewWarehouseHistory
      >(
        `/admin/support-simulations/review/threads/${encodeURIComponent(threadId!)}/warehouse-history?${params}`,
        { signal },
      );
      const validTicket = (ticket: { review_ticket_id?: unknown } | null) =>
        ticket === null ||
        (typeof ticket.review_ticket_id === "string" &&
          /^[0-9a-f]{64}$/.test(ticket.review_ticket_id));
      if (
        history.thread_id !== threadId ||
        history.action_blocked !== true ||
        history.limit !== 50 ||
        history.has_more !== Boolean(history.next_cursor) ||
        (history.next_cursor !== null &&
          (!history.next_cursor.length ||
            history.next_cursor.length > 640 ||
            history.next_cursor === pageParam)) ||
        !validTicket(history.current_root) ||
        !history.tickets.every(validTicket)
      )
        throw new Error("Warehouse history identity changed");
      return history;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage, _pages, _lastPageParam, pageParams) =>
      lastPage.has_more &&
      lastPage.next_cursor &&
      !pageParams.includes(lastPage.next_cursor)
        ? lastPage.next_cursor
        : undefined,
    enabled: !!actorId && !!threadId,
    retry: false,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useSupportReviewWarehouseTicket(
  threadId: string | undefined,
  reviewTicketId: string | undefined,
  actorId: string | undefined,
) {
  return useInfiniteQuery({
    queryKey: [
      "admin",
      "support-review-warehouse-ticket",
      actorId,
      threadId,
      reviewTicketId,
    ],
    queryFn: async ({ signal, pageParam }) => {
      const params = new URLSearchParams({ limit: "50" });
      if (pageParam) params.set("cursor", pageParam);
      const page = await apiFetch<
        import("@/types/supportReview").SupportReviewWarehouseTicketPage
      >(
        `/admin/support-simulations/review/threads/${encodeURIComponent(threadId!)}/warehouse-history/${encodeURIComponent(reviewTicketId!)}?${params}`,
        { signal },
      );
      const nextCursor = page.next_cursor;
      if (
        page.thread_id !== threadId ||
        page.ticket.review_ticket_id !== reviewTicketId ||
        page.action_blocked !== true ||
        page.limit !== 50 ||
        page.has_more !== Boolean(nextCursor) ||
        (nextCursor !== null &&
          (!nextCursor.length ||
            nextCursor.length > 640 ||
            nextCursor === pageParam))
      )
        throw new Error("Warehouse ticket page identity changed");
      return page;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage, _pages, _lastPageParam, pageParams) =>
      lastPage.has_more &&
      lastPage.next_cursor &&
      !pageParams.includes(lastPage.next_cursor)
        ? lastPage.next_cursor
        : undefined,
    enabled:
      !!actorId &&
      !!threadId &&
      !!reviewTicketId &&
      /^[0-9a-f]{64}$/.test(reviewTicketId),
    retry: false,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useSupportReviewRelatedTicket(
  threadId: string | undefined,
  reviewTicketId: string | undefined,
  actorId: string | undefined,
) {
  return useInfiniteQuery({
    queryKey: [
      "admin",
      "support-review-related-ticket",
      actorId,
      threadId,
      reviewTicketId,
    ],
    queryFn: async ({ signal, pageParam }) => {
      const page = await apiFetch<
        import("@/types/supportReview").SupportReviewRelatedTicketPage
      >(
        `/admin/support-simulations/review/threads/${encodeURIComponent(threadId!)}/related/${encodeURIComponent(reviewTicketId!)}?limit=50&offset=${pageParam}`,
        { signal },
      );
      if (
        page.thread_id !== threadId ||
        page.ticket.review_ticket_id !== reviewTicketId ||
        page.offset !== pageParam ||
        page.limit !== 50
      )
        throw new Error("Related ticket page identity changed");
      return page;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.offset + lastPage.limit : undefined,
    enabled: !!actorId && !!threadId && !!reviewTicketId,
    retry: false,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
import type {
  DirectoryPage,
  DirectoryMessagePage,
  DirectoryStage,
  DirectoryChannel,
} from "@/types/supportDirectory";

export function useSupportPersonalTodo(actorId: string | undefined) {
  return useInfiniteQuery({
    queryKey: ["admin", "support-personal-todo", actorId],
    enabled: !!actorId,
    initialPageParam: null as string | null,
    gcTime: 0,
    retry: false,
    queryFn: async ({ pageParam, signal }) => {
      const params = new URLSearchParams({ limit: "50" });
      if (pageParam) params.set("cursor", pageParam);
      const page = await apiFetch<DirectoryPage>(
        `/admin/support-simulations/review/todos?${params}`,
        { signal, cache: "no-store" },
      );
      if (
        !Array.isArray(page.items) ||
        !Array.isArray(page.coverage?.limitations) ||
        !Array.isArray(page.coverage?.sources) ||
        typeof page.has_more !== "boolean" ||
        (page.has_more
          ? typeof page.next_cursor !== "string" ||
            !page.next_cursor ||
            page.next_cursor === pageParam
          : page.next_cursor !== null) ||
        page.items.some(
          (item) =>
            item.read_only !== true ||
            typeof item.id !== "string" ||
            item.status === "closed",
        )
      )
        throw new Error("Invalid personal task queue");
      return page;
    },
    getNextPageParam: (page) => (page.has_more ? page.next_cursor : undefined),
  });
}

export function useSupportDirectory(
  actorId: string | undefined,
  filters: {
    q: string;
    stage: DirectoryStage | "all";
    channel: DirectoryChannel | "all";
  },
  options: { enabled?: boolean; limit?: number } = {},
) {
  const limit = options.limit ?? 50;
  return useInfiniteQuery({
    queryKey: ["admin", "support-directory", actorId, filters, limit],
    enabled: !!actorId && options.enabled !== false,
    initialPageParam: null as string | null,
    // Return navigation can paint the same reviewer's previous results immediately.
    // Always revalidate on mount; failed refreshes are hidden by DirectoryLane.
    gcTime: 5 * 60_000,
    staleTime: 0,
    refetchOnMount: "always",
    retry: false,
    queryFn: async ({ pageParam, signal }) => {
      const params = new URLSearchParams({ ...filters, limit: String(limit) });
      if (pageParam) params.set("cursor", pageParam);
      const page = await apiFetch<DirectoryPage>(
        `/admin/support-simulations/review/directory?${params}`,
        { signal, cache: "no-store" },
      );
      if (
        !Array.isArray(page.items) ||
        !Array.isArray(page.coverage?.limitations) ||
        !Array.isArray(page.coverage?.sources) ||
        page.coverage.sources.some(
          (source) =>
            typeof source.available !== "boolean" ||
            typeof source.source_label !== "string",
        ) ||
        typeof page.has_more !== "boolean" ||
        (page.has_more
          ? typeof page.next_cursor !== "string" ||
            !page.next_cursor ||
            page.next_cursor === pageParam
          : page.next_cursor !== null) ||
        page.items.some(
          (item) =>
            item.read_only !== true ||
            typeof item.id !== "string" ||
            !["email", "portal", "sms", "healthie"].includes(item.channel) ||
            (filters.channel !== "all" && item.channel !== filters.channel) ||
            (filters.stage !== "all" && item.status !== filters.stage) ||
            ![
              "open",
              "waiting_response",
              "waiting_pharmacy",
              "waiting_clinician",
              "closed",
              "unknown",
              "waiting_unspecified",
            ].includes(item.status),
        )
      )
        throw new Error("Invalid thread directory");
      return page;
    },
    getNextPageParam: (page) => (page.has_more ? page.next_cursor : undefined),
  });
}

export function useSupportDirectoryMessages(
  actorId: string | undefined,
  threadId: string,
) {
  return useInfiniteQuery({
    queryKey: ["admin", "support-directory", actorId, threadId, "messages"],
    enabled: !!actorId && !!threadId,
    initialPageParam: null as string | null,
    gcTime: 0,
    retry: false,
    queryFn: async ({ pageParam, signal }) => {
      const params = new URLSearchParams({ limit: "50" });
      if (pageParam) params.set("cursor", pageParam);
      const page = await apiFetch<DirectoryMessagePage>(
        `/admin/support-simulations/review/directory/${encodeURIComponent(threadId)}/messages?${params}`,
        { signal, cache: "no-store" },
      );
      if (
        page.read_only !== true ||
        !Array.isArray(page.items) ||
        typeof page.content_available !== "boolean" ||
        typeof page.has_more !== "boolean" ||
        (page.has_more
          ? typeof page.next_cursor !== "string" ||
            !page.next_cursor ||
            page.next_cursor === pageParam
          : page.next_cursor !== null) ||
        (!page.content_available && page.items.length > 0) ||
        page.items.some(
          (item) =>
            typeof item.id !== "string" || typeof item.content !== "string",
        )
      )
        throw new Error("Invalid read-only thread history");
      return page;
    },
    getNextPageParam: (page) => (page.has_more ? page.next_cursor : undefined),
  });
}

// Read-only delivery exceptions use their real source IDs, never model-run IDs.
export function useSmsDeliveryAttention(actorId: string | undefined) {
  return useInfiniteQuery({
    queryKey: ["admin", "sms-delivery-attention", actorId],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam, signal }) => {
      const params = new URLSearchParams({ limit: "25" });
      if (pageParam) params.set("cursor", pageParam);
      const page = await apiFetch<import("@/types/smsDeliveryAttention").SmsDeliveryAttentionPage>(
        `/admin/customer-service-history/sms-delivery-attention?${params}`,
        { signal },
      );
      if (!page || !Array.isArray(page.items) || !page.coverage ||
          typeof page.coverage.scan_complete !== "boolean" ||
          !Array.isArray(page.coverage.limitations) || typeof page.has_more !== "boolean" ||
          (page.has_more && (typeof page.next_cursor !== "string" || !page.next_cursor))) {
        throw new Error("Delivery attention response unavailable");
      }
      return page;
    },
    getNextPageParam: (page) => page?.has_more ? page.next_cursor ?? undefined : undefined,
    enabled: !!actorId,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
  });
}
